import { isDefined } from '@hover/utilities';

import { getPalettes } from 'src/modules/homeownerApp/components/Palette/data';
import type { ProductCatalogProductFragment } from 'src/modules/homeownerApp/graphql/generated';
import type { ProductCatalogVariationFragment } from 'src/shared/graphql/generated';

/**
 * DesignerProduct represents a product usable by Designer for customization. We
 * cannot use types directly from GraphQL because we query Product Catalog very
 * differently to display edits vs getting a list of products for customize.
 *
 * This abstraction allows us to get the same type output for
 * ProductCatalogVariationFragment and for ProductCatalogProductFragment. Since
 * we are creating our own type, we can provide stronger type guarantees for
 * specific fields. For example, GraphQL types indicate that a product does not
 * always have a provider. In the case of DesignerProducts, we must always have
 * a provider and any Product Catalog missing a provider is misconfigured. While
 * converting types to DesignerProduct, any Product Catalog product not meeting
 * the type requirements is discarded. Stronger type guarantees simplifies our
 * codebase as the number of cases that must be handled shrinks.
 */
export type DesignerProduct = {
  $type: 'color' | 'material';
  product: {
    id: string;
  };
  provider: {
    id: string;
    name: string;
  };
  variation: {
    id: string;
    name: string;
    manufacturerUrl?: string;
    thumbnail?: string;
  };
  color?: {
    id: string;
    name: string;
    hex: string;
  };
  facets: { name: string; value: string }[];
  palettes: string[];
  prompt?: {
    id?: string;
    name?: string;
  };
};

type Facet = {
  id: string;
  facetType?: string;
  value?: string;
  facetValue?: {
    id: string;
    value: string;
    facet: {
      name: string;
    };
  } | null;
};

type NormalizedFacet = {
  name: string;
  value: string;
};

/**
 * Facets can be defined in a couple different ways in Product Catalog. This is
 * not useful for the front end. This method normalizes facet definition for use
 * in the front end.
 */
const normalizeFacets = <T extends Facet>(facets: T[]): NormalizedFacet[] =>
  facets
    .map(facet => {
      const name = facet.facetValue?.facet.name || facet.facetType;
      const value = facet.facetValue?.value || facet.value;

      if (!name || !value) return null;

      return {
        name,
        value,
      };
    })
    .filter(isDefined);

const productCatalogProductFragmentToDesignerProducts = (
  product: ProductCatalogProductFragment,
): DesignerProduct[] =>
  product.variations
    .map(variation => {
      if (!product.provider?.id || !product.provider.name) {
        return null;
      }

      const color = variation.genAiColor
        ? {
            id: variation.genAiColor.id,
            hex: variation.genAiColor.hex,
            name: variation.genAiColor.name,
          }
        : undefined;

      const prompt = variation.aiPrompt
        ? { id: variation.aiPrompt.id, name: variation.aiPrompt.name }
        : undefined;

      const facets = variation.variationFacets
        ? normalizeFacets(variation.variationFacets)
        : [];

      const palettes = getPalettes(
        product.provider.id,
        variation.variationFacets,
      );

      return {
        $type: variation.image?.redirectUrl ? 'material' : 'color',
        product: {
          id: product.id,
        },
        provider: {
          id: product.provider.id,
          name: product.provider.name,
        },
        variation: {
          id: variation.id,
          name: variation.name,
          manufacturerUrl: variation.manufacturerUrl ?? undefined,
          thumbnail: variation.image?.redirectUrl ?? undefined,
        },
        color,
        facets,
        palettes,
        prompt,
      } satisfies DesignerProduct;
    })
    .filter(isDefined);

const productCatalogVariationFragmentToDesignerProducts = (
  variation: ProductCatalogVariationFragment,
): DesignerProduct[] => {
  if (!variation.product.id) {
    return [];
  }
  if (!variation.product.provider?.id || !variation.product.provider.name) {
    return [];
  }

  const color = variation.genAiColor
    ? {
        id: variation.genAiColor.id,
        hex: variation.genAiColor.hex,
        name: variation.genAiColor.name,
      }
    : undefined;

  const prompt = variation.aiPrompt
    ? { id: variation.aiPrompt.id, name: variation.aiPrompt.name }
    : undefined;

  const facets = variation.variationFacets
    ? normalizeFacets(variation.variationFacets)
    : [];

  const palettes = getPalettes(
    variation.product.provider.id,
    variation.variationFacets,
  );

  return [
    {
      $type: variation.image?.redirectUrl ? 'material' : 'color',
      product: {
        id: variation.product.id,
      },
      provider: {
        id: variation.product.provider.id,
        name: variation.product.provider.name,
      },
      variation: {
        id: variation.id,
        name: variation.name,
        manufacturerUrl: variation.manufacturerUrl ?? undefined,
        thumbnail: variation.image?.redirectUrl ?? undefined,
      },
      color,
      facets,
      palettes,
      prompt,
    },
  ];
};

export const getFacetValue = (
  product: DesignerProduct,
  facetName: string,
): string | undefined =>
  product.facets.find(facet => facet.name === facetName)?.value;

export const isGenericProduct = (product: DesignerProduct) =>
  !!product.provider.name.match('Generic');

export const isPaintProduct = (
  product: DesignerProduct | undefined | null,
): product is DesignerProduct => product?.$type === 'color';

export const isMaterialProduct = (
  product: DesignerProduct | undefined | null,
): product is DesignerProduct => product?.$type === 'material';

export const toDesignerProduct = (
  data: ProductCatalogVariationFragment,
): DesignerProduct | null =>
  productCatalogVariationFragmentToDesignerProducts(data)[0];

export const toDesignerProducts = (
  data: ProductCatalogProductFragment | ProductCatalogVariationFragment,
): DesignerProduct[] => {
  if (data.__typename === 'ProductCatalogProduct') {
    return productCatalogProductFragmentToDesignerProducts(data);
  }

  if (data.__typename === 'ProductCatalogVariation') {
    return productCatalogVariationFragmentToDesignerProducts(data);
  }

  return [];
};
